/**
 * Chat types for AccountsGenie
 * Following PRD Phase 3 specifications
 */

// Re-export visualization types from the canonical source
export type {
  VisualizationType,
  VisualizationData,
  BarChartVisualization,
  PieChartVisualization,
  LineChartVisualization,
  AreaChartVisualization,
  TableVisualization,
  SummaryCardVisualization,
  BulletListVisualization,
  TrendInfo,
  FormatType,
} from '../components/chat/visualizations/types';

// Import for use in Message interface
import type { VisualizationData } from '../components/chat/visualizations/types';

export interface FileAttachment {
  file_id: string;
  public_url: string;
  filename: string;
  content_type: string;
  file_size_bytes: number;
}

export interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  timestamp: Date;
  status?: 'sending' | 'sent' | 'error';
  error?: string;
  // Special message types for inline components
  messageType?: 'text' | 'plan' | 'execution' | 'execution-result' | 'agent-status' | 'visualization';
  planData?: any; // Plan approval data
  executionData?: any; // Execution monitor data
  executionResultData?: any; // Execution result data
  attachments?: FileAttachment[]; // File attachments
  visualizationData?: VisualizationData; // Data visualization from agent
  stepSummary?: { steps: number; duration: number }; // Step count and duration for this response
}

export interface ChatSession {
  id: string;
  messages: Message[];
  createdAt: Date;
  updatedAt: Date;
  title?: string;
}

export interface ChatApiResponse {
  response: string;
  sessionId: string;
  timestamp: string;
  messageId?: string;
}

// AA WebSocket Message Types
export interface WebSocketMessage {
  type: 'send_message' | 'approve_plan' | 'get_status' | 'message' | 'plan' | 'execution_started' | 'execution_update' | 'execution_complete' | 'error' | 'status' | 'new_step_added' | 'steps_added';
  // Client to server messages
  content?: string; // for send_message
  plan_id?: string; // for approve_plan
  approved?: boolean; // for approve_plan
  // Server to client messages
  message?: Message; // for message type
  plan?: PlanDetails; // for plan type
  task_id?: string; // for execution_update
  status?: string; // for execution_update and status
  progress?: number; // for execution_update
  result?: any; // for execution_complete
  error?: string; // for error type
  connected?: boolean; // for status type
  session_id?: string; // for status type
  timestamp?: string;

  // Dynamic step addition fields
  step?: {
    id: string;
    step: number;
    description: string;
    tool?: string;
    status: string;
    parent_step?: number;
    dependencies: number[];
    risk_level?: string;
  };
  steps?: Array<{
    id: string;
    step: number;
    description: string;
    tool?: string;
    status: string;
    parent_step?: number;
    dependencies: number[];
    risk_level?: string;
  }>;
  parent_step?: number;
  insert_after?: number;
}

// Plan and Execution Types for AA Integration
export interface PlanDetails {
  id: string;
  tasks: TaskDetail[];
  estimated_time: number;
  requires_approval: boolean;
}

export interface TaskDetail {
  id: string;
  step?: number;
  description: string;
  tool: string;
  dependencies: string[];
  execution_type: 'parallel' | 'series';
  status?: string;
  risk_level?: string;
  parent_step?: number;
}

export interface ExecutionUpdate {
  task_id?: string;  // Optional as initial events don't have task_id
  status: 'starting' | 'executing' | 'started' | 'progressing' | 'completed' | 'failed' | 'error';
  progress: number;
  // Extended properties for different event types
  event?: string;
  type?: string;
  plan_id?: string;
  tasks?: any[];
  taskId?: string;
  result?: any;
  output?: any;
  error?: string;
  error_message?: string;
  message?: string;
  percentage?: number;
  step_info?: {
    step_id?: string;
    id?: string;
    status?: string;
    result?: any;
    error?: string;
  };
  // Metadata for dynamic step addition
  metadata?: {
    is_new_step?: boolean;
    parent_step?: number;
    inserted_at?: number;
    description?: string;
    tool?: string;
    dependencies?: number[];
  };
}

export interface ExampleCommand {
  title: string;
  command: string;
  description?: string;
}

// Example commands from PRD - Updated with Tally-specific queries
export const EXAMPLE_COMMANDS: ExampleCommand[] = [
  {
    title: "Today's Sales",
    command: "Show me today's sales summary from Tally",
    description: "View detailed sales data for the current day including invoices and receipts"
  },
  {
    title: "Create Payment Voucher",
    command: "Create a payment voucher for vendor ABC Company amount 50000",
    description: "Generate a new payment voucher entry in Tally"
  },
  {
    title: "Cash & Bank Balance",
    command: "What's my current cash and bank balance?",
    description: "Check current cash in hand and bank account balances"
  },
  {
    title: "Profit & Loss Report",
    command: "Generate profit and loss statement for last month",
    description: "View comprehensive P&L report with revenue and expense details"
  },
  {
    title: "Outstanding Receivables",
    command: "Show me outstanding receivables from customers",
    description: "List of pending payments from customers with aging analysis"
  },
  {
    title: "Inventory Status",
    command: "What's my current inventory status?",
    description: "View stock levels, low stock items, and inventory valuation"
  },
  {
    title: "GST Reports",
    command: "Generate GST return summary for this month",
    description: "View GST liability, input credits, and return filing details"
  },
  {
    title: "Trial Balance",
    command: "Show me the trial balance as of today",
    description: "View current trial balance with all account balances"
  }
];

export const CHARACTER_LIMIT = 1000;